/**
 * Financial Dashboard.
 *
 * Every figure here is aggregated in Postgres and arrives pre-summed. The
 * browser filters nothing and totals nothing — it draws what the server already
 * decided this user may see.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  CLAIM_STATUSES,
  money,
  periodLabel,
  statusLabel,
  type ClaimStatus,
} from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { StatusBadge } from '@/components/Badge';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { ErrorNote, QueuePanel } from '@/components/QueueKit';
import { LoadingBlock } from '@/components/Spinner';
import { StatCards, type StatCard } from '@/components/StatCards';
import { useToast } from '@/components/Toast';
import { downloadWorkbook, todayStamp, type CellValue } from '@/components/admin/sheets';
import { useAsync } from '@/hooks/useAsync';
import type { FinancialDashboardData } from '@/types/claims';
import '@/styles/pages.css';

interface Filters {
  from: string;
  to: string;
  department: string;
  status: string;
}

const EMPTY: Filters = { from: '', to: '', department: '', status: '' };

export function FinancialDashboardPage(): ReactNode {
  const { toast } = useToast();
  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);

  const load = useCallback(
    () =>
      api.reporting.financialDashboard<FinancialDashboardData>({
        ...(applied.from ? { from: applied.from } : {}),
        ...(applied.to ? { to: applied.to } : {}),
        ...(applied.department ? { department: applied.department } : {}),
        ...(applied.status ? { status: applied.status } : {}),
      }),
    [applied],
  );

  const { data, loading, error, reload } = useAsync<FinancialDashboardData>(load, [applied]);

  const departments = useMemo(
    () => (data?.byDepartment ?? []).map((d) => d.department).sort(),
    [data],
  );

  const exportAll = useCallback(async (): Promise<void> => {
    if (!data) return;
    try {
      await downloadWorkbook(`GDEC_Financial_Dashboard_${todayStamp()}.xlsx`, [
        {
          name: 'Summary',
          rows: [
            ['Metric', 'Value'],
            ['Claims', data.totals.claimCount],
            ['Total Claimed', data.totals.claimedTotal],
            ['Total Finance Reviewed', data.totals.reviewedTotal],
            ['Total Approved', data.totals.approvedTotal],
            ['Total Released', data.totals.releasedTotal],
          ] satisfies CellValue[][],
        },
        {
          name: 'By Stage',
          rows: [
            ['Stage', 'Claims', 'Claimed Value'],
            ...data.byStatus.map((s): CellValue[] => [s.status, s.count, s.claimedTotal]),
          ],
        },
        {
          name: 'By Department',
          rows: [
            ['Department', 'Claims', 'Claimed', 'Released'],
            ...data.byDepartment.map((d): CellValue[] => [
              d.department,
              d.count,
              d.claimedTotal,
              d.releasedTotal,
            ]),
          ],
        },
        {
          name: 'By Category',
          rows: [
            ['Category', 'Receipts', 'Total'],
            ...data.byCategory.map((c): CellValue[] => [c.category, c.count, c.total]),
          ],
        },
        {
          name: 'By Period',
          rows: [
            ['Period', 'Claims', 'Claimed', 'Released'],
            ...data.byPeriod.map((p): CellValue[] => [
              p.period,
              p.count,
              p.claimedTotal,
              p.releasedTotal,
            ]),
          ],
        },
      ]);
      toast('Financial dashboard exported.', 'ok');
    } catch (err) {
      toast(errorMessage(err), 'err');
    }
  }, [data, toast]);

  if (loading && data === null) return <LoadingBlock label="Aggregating claims…" />;

  const totals = data?.totals;
  const cards: StatCard[] = [
    { n: totals?.claimCount ?? 0, l: 'Claims' },
    { n: money(totals?.claimedTotal ?? 0), l: 'Claimed' },
    { n: money(totals?.reviewedTotal ?? 0), l: 'Finance Reviewed' },
    { n: money(totals?.approvedTotal ?? 0), l: 'Approved' },
    { n: money(totals?.releasedTotal ?? 0), l: 'Released', accent: true },
  ];

  return (
    <>
      <ErrorNote message={error} />

      <div className="panel">
        <h3>
          Financial Dashboard
          <div className="actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => void exportAll()}
              disabled={!data}
            >
              ⬇ Export
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={reload}>
              Refresh
            </button>
          </div>
        </h3>

        <div className="filter-row">
          <div className="field">
            <label htmlFor="fd-from">From period</label>
            <input
              id="fd-from"
              type="month"
              value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="fd-to">To period</label>
            <input
              id="fd-to"
              type="month"
              value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="fd-dept">Department</label>
            <select
              id="fd-dept"
              value={draft.department}
              onChange={(e) => setDraft({ ...draft, department: e.target.value })}
            >
              <option value="">All departments</option>
              {departments.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="fd-status">Stage</label>
            <select
              id="fd-status"
              value={draft.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value })}
            >
              <option value="">All stages</option>
              {CLAIM_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s)}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setApplied(draft)}>
              Apply
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setDraft(EMPTY);
                setApplied(EMPTY);
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <StatCards cards={cards} />

      <div className="panel">
        <h3>Claimed vs Approved vs Released</h3>
        <ComparisonBars
          claimed={totals?.claimedTotal ?? 0}
          approved={totals?.approvedTotal ?? 0}
          released={totals?.releasedTotal ?? 0}
        />
      </div>

      <QueuePanel title="By stage">
        <DataTable
          rows={data?.byStatus ?? []}
          columns={
            [
              { key: 'status', header: 'Stage', cell: (r) => <StatusBadge status={r.status} /> },
              { key: 'count', header: 'Claims', align: 'right', cell: (r) => r.count },
              {
                key: 'value',
                header: 'Claimed Value',
                align: 'right',
                cell: (r) => money(r.claimedTotal),
              },
            ] satisfies ReadonlyArray<
              Column<{ status: ClaimStatus; count: number; claimedTotal: number }>
            >
          }
          rowKey={(r) => r.status}
          empty={<EmptyState icon="📊" title="No claims match these filters." />}
        />
      </QueuePanel>

      <QueuePanel title="By department">
        <DataTable
          rows={data?.byDepartment ?? []}
          columns={[
            { key: 'department', header: 'Department', cell: (r) => r.department },
            { key: 'count', header: 'Claims', align: 'right', cell: (r) => r.count },
            { key: 'claimed', header: 'Claimed', align: 'right', cell: (r) => money(r.claimedTotal) },
            {
              key: 'released',
              header: 'Released',
              align: 'right',
              cell: (r) => money(r.releasedTotal),
            },
          ]}
          rowKey={(r) => r.department}
          empty={<EmptyState icon="📊" title="Nothing to summarise." />}
        />
      </QueuePanel>

      <QueuePanel title="By expense category">
        <DataTable
          rows={data?.byCategory ?? []}
          columns={[
            { key: 'category', header: 'Category', cell: (r) => r.category || '—' },
            { key: 'count', header: 'Receipts', align: 'right', cell: (r) => r.count },
            { key: 'total', header: 'Total', align: 'right', cell: (r) => money(r.total) },
          ]}
          rowKey={(r) => r.category || 'uncategorised'}
          empty={<EmptyState icon="📊" title="Nothing to summarise." />}
        />
      </QueuePanel>

      <QueuePanel title="By period">
        <DataTable
          rows={data?.byPeriod ?? []}
          columns={[
            { key: 'period', header: 'Period', cell: (r) => periodLabel(r.period) },
            { key: 'count', header: 'Claims', align: 'right', cell: (r) => r.count },
            { key: 'claimed', header: 'Claimed', align: 'right', cell: (r) => money(r.claimedTotal) },
            {
              key: 'released',
              header: 'Released',
              align: 'right',
              cell: (r) => money(r.releasedTotal),
            },
          ]}
          rowKey={(r) => r.period}
          empty={<EmptyState icon="📊" title="Nothing to summarise." />}
        />
      </QueuePanel>
    </>
  );
}

/**
 * Three bars scaled against the largest value.
 *
 * Deliberately not a charting library: this is one comparison, and the shape of
 * the gap between claimed and released is the whole point.
 */
function ComparisonBars({
  claimed,
  approved,
  released,
}: {
  claimed: number;
  approved: number;
  released: number;
}): ReactNode {
  const max = Math.max(claimed, approved, released);
  const bars = [
    { label: 'Claimed', value: claimed, tone: 'bar-claimed' },
    { label: 'Approved', value: approved, tone: 'bar-approved' },
    { label: 'Released', value: released, tone: 'bar-released' },
  ];

  if (max === 0) return <EmptyState icon="📊" title="No amounts to compare yet." />;

  return (
    <div className="bar-chart">
      {bars.map((b) => (
        <div className="bar-row" key={b.label}>
          <span className="bar-label">{b.label}</span>
          <div className="bar-track">
            <div
              className={`bar-fill ${b.tone}`}
              style={{ width: `${Math.max((b.value / max) * 100, b.value > 0 ? 1 : 0)}%` }}
              role="img"
              aria-label={`${b.label}: ${money(b.value)}`}
            />
          </div>
          <span className="bar-value">{money(b.value)}</span>
        </div>
      ))}
    </div>
  );
}
